import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Pagination,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Card,
  CardContent,
  Stack,
  Fade,
  Zoom,
  Container,
  useTheme,
  alpha,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack,
  MoreVert,
  ConfirmationNumber,
  Schedule,
  Assignment,
  CheckCircle,
  Pending,
  Close,
  Business,
  AddCircleOutline,
} from "@mui/icons-material";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Tooltip, Dialog, DialogTitle } from "@mui/material";

import { useRef } from "react";
import TicketPage from "./TicketPage";

const TicketHistory = () => {
  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const theme = useTheme();
  const isMenuOpen = Boolean(anchorEl);
  // Get logged in user safely
  const userDetails = useSelector((state) => state?.Auth?.user) || {};
  const email = userDetails?.email;
  const mobile = userDetails?.contactNumber;
  const navigate = useNavigate();

  const isMobile = useMediaQuery("(max-width:1000px)");

  const [openForm, setOpenForm] = useState(false);
  const submitTicketRef = useRef(null);
   const TENANT_ID = "7698a247-5850-45b5-92e4-848f2bf72d67";
   const user_id =  userDetails?.type === "CP" ? userDetails?.cpDisplayId : userDetails?.fosDisplayId;


  const fetchTickets = async () => {
    if (!email || !mobile) return;

    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_HELPDESK_API_URL}/api/user/getTicketByCustomerMobileandEmail`,
       { email, mobile, page, pageSize, tenant_id: TENANT_ID ,id: user_id }
      );

      if (response.data.success) {
        setTickets(response.data.data);
      } else {
        setTickets([]);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event, ticketId) => {
    setAnchorEl(event.currentTarget);
    setSelectedTicketId(ticketId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTicketId(null);
  };

  const handleCloseTicket = async () => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_HELPDESK_API_URL}/api/user/ticket/update-status`,
        {
          status: "closed",
          ticketId: selectedTicketId,
        }
      );

      if (res.data.success) {
        toast.success("Ticket closed successfully");
        handleMenuClose();
        fetchTickets();
      } else {
        toast.error("Failed to close ticket");
      }
    } catch (err) {
      console.error("Error closing ticket:", err);
      toast.error("Error occurred while closing ticket");
    }
  };

  const formatDate = (iso) => new Date(iso).toLocaleString("en-IN");

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "resolved":
        return "success";
      case "open":
        return "warning";
      case "closed":
        return "default";
      default:
        return "primary";
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "resolved":
        return <CheckCircle fontSize="small" />;
      case "open":
        return <Pending fontSize="small" />;
      case "closed":
        return <Close fontSize="small" />;
      default:
        return <ConfirmationNumber fontSize="small" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case "high":
        return theme.palette.error.main;
      case "medium":
        return theme.palette.warning.main;
      case "low":
        return theme.palette.success.main;
      default:
        return theme.palette.info.main;
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [email, mobile, page, TENANT_ID, user_id]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.1
        )} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            background: "linear-gradient(135deg,  #1570EF 0%,  #667eea  100%)",
            color: "white",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={isMobile ? 1 : 2}
            sx={{
              flexWrap: "nowrap", // force all items on a single row
              overflow: "hidden", // handle small screens
            }}
          >
            {/* Back Button */}
            <IconButton
              onClick={() => window.history.back()}
              sx={{
                color: "white",
                "&:hover": {
                  bgcolor: alpha("#fff", 0.1),
                },
              }}
            >
              <ArrowBack sx={{ fontSize: isMobile ? 24 : 30 }} />
            </IconButton>

            {/* Icon */}
            {!isMobile && <ConfirmationNumber sx={{ fontSize: 28 }} />}

            {/* Title */}
            <Typography
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? 18 : 28,
                flexGrow: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Ticket History
            </Typography>

            {/* Create Button */}
            <Tooltip
              title={
                <Typography sx={{ fontSize: "1rem", fontFamily: "Rubik" }}>
                  Create Ticket
                </Typography>
              }
              arrow
              placement="bottom"
            >
              <IconButton
                onClick={() => setOpenForm(true)}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                  width: isMobile ? 34 : 40,
                  height: isMobile ? 34 : 40,
                }}
              >
                <AddCircleOutline
                  sx={{ fontSize: isMobile ? 20 : 26, color: "#fff" }}
                />
              </IconButton>
            </Tooltip>

            {/* Ticket Count */}
            <Avatar
              sx={{
                bgcolor: "white",
                color: "#1570EF",
                width: isMobile ? 34 : 40,
                height: isMobile ? 34 : 40,
              }}
            >
              <Typography
                sx={{
                  fontSize: isMobile ? "2.2rem" : "1.5rem",
                  fontWeight: 700,
                }}
              >
                {tickets.length}
              </Typography>
            </Avatar>
          </Stack>
        </Paper>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
              gap: 2,
            }}
          >
            <CircularProgress size={60} thickness={4} />
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontFamily: "Rubik" }}
            >
              Loading your tickets...
            </Typography>
          </Box>
        ) : tickets.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: "center",
              borderRadius: 3,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
            }}
          >
            <ConfirmationNumber
              sx={{ fontSize: 80, color: "text.secondary", mb: 2 }}
            />
            <Typography
              variant="h4"
              color="text.secondary"
              sx={{ fontFamily: "Rubik" }}
              gutterBottom
            >
              No tickets found
            </Typography>
            <Typography
              variant={isMobile ? "h5" : "h6"}
              sx={{ fontFamily: "Rubik" }}
              color="text.secondary"
            >
              You haven't created any tickets yet.
            </Typography>
          </Paper>
        ) : (
          <Fade in timeout={800}>
            <Box>
              <Grid container spacing={3}>
                {tickets.map((ticket, index) => (
                  <Grid item xs={12} md={6} lg={4} key={ticket.id}>
                    <Zoom in timeout={600 + index * 100}>
                      <Card
                        elevation={0}
                        onClick={() => navigate(`${ticket.id}`)}
                        sx={{
                          borderRadius: 3,
                          border: `1px solid ${alpha(
                            theme.palette.divider,
                            0.1
                          )}`,
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: "pointer",
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: `0 20px 40px ${alpha(
                              theme.palette.common.black,
                              0.1
                            )}`,
                          },
                          fontFamily: "Rubik",
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          {/* Ticket Header */}
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            mb={2}
                          >
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  mb: 0.5,
                                  fontFamily: "Rubik",
                                  fontSize: isMobile ? "2.5rem" : "1.5rem",
                                }}
                              >
                                {ticket.display_id}
                              </Typography>
                              <Chip
                                icon={getStatusIcon(ticket.status)}
                                label={ticket.status.toUpperCase()}
                                color={getStatusColor(ticket.status)}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  fontFamily: "Rubik",
                                  fontSize: isMobile ? "2rem" : "1rem",
                                }}
                              />
                            </Box>
                            {ticket.status === "resolved" && (
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation(); // stop card click
                                  handleMenuClick(e, ticket.id);
                                }}
                                sx={{
                                  color: "text.secondary",
                                  fontFamily: "Rubik",
                                  fontSize: "2rem",
                                  "&:hover": {
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                  },
                                }}
                              >
                                <MoreVert />
                              </IconButton>
                            )}
                          </Stack>

                          <Divider sx={{ my: 2 }} />

                          {/* Ticket Details */}
                          <Stack spacing={2}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Assignment
                                sx={{
                                  color: "text.secondary",
                                  fontSize: 20,
                                  fontFamily: "Rubik",
                                }}
                              />
                              <Typography
                                // variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  fontFamily: "Rubik",
                                  fontSize: isMobile ? "2rem" : "1.2rem",
                                }}
                              >
                                {ticket.category_name}
                              </Typography>
                              {ticket.subcategory_name && (
                                <Typography
                                  // variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    fontFamily: "Rubik",
                                    fontSize: isMobile ? "2rem" : "1.2rem",
                                  }}
                                >
                                  • {ticket.subcategory_name}
                                </Typography>
                              )}
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Business
                                sx={{
                                  color: "text.secondary",
                                  fontSize: 20,
                                  fontFamily: "Rubik",
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontFamily: "Rubik",
                                  fontSize: isMobile ? "2rem" : "1.2rem",
                                }}
                              >
                                {ticket.department_name}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Schedule
                                sx={{
                                  color: "text.secondary",
                                  fontSize: 20,
                                  fontFamily: "Rubik",
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontFamily: "Rubik",
                                  fontSize: isMobile ? "2rem" : "1.2rem",
                                }}
                              >
                                {formatDate(ticket.created_at)}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {/* <Priority sx={{ color: getPriorityColor(ticket.priority_level), fontSize: 20 }} /> */}
                              <Typography
                                variant="body2"
                                sx={{
                                  color: getPriorityColor(
                                    ticket.priority_level
                                  ),
                                  fontWeight: 600,
                                  fontFamily: "Rubik",
                                  fontSize: isMobile ? "2rem" : "1.2rem",
                                }}
                              >
                                {ticket.priority_level} Priority
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Zoom>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                  }}
                >
                  <Pagination
                    count={Math.ceil(
                      tickets.length < pageSize ? page : page + 1
                    )}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    size="large"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: isMobile ? "2rem" : "1.2rem",
                      },
                      "& .MuiPaginationItem-icon": {
                        fontSize: isMobile ? "3rem" : "2rem", // 🔺 arrow icon size
                      },
                    }}
                  />
                </Paper>
              </Box>
            </Box>
          </Fade>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 8,
            sx: {
              borderRadius: 2,
              minWidth: 180,
              "& .MuiMenuItem-root": {
                borderRadius: 1,
                mx: 1,
                my: 0.5,
              },
            },
          }}
        >
          <MenuItem
            onClick={handleCloseTicket}
            sx={{
              color: "error.main",
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            <Close sx={{ mr: 1, fontSize: 20 }} />
            Close Ticket
          </MenuItem>
        </Menu>
      </Container>
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            height: "90vh",
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Rubik",
            fontWeight: 700,
            fontSize: isMobile ? "2rem" : "1.5rem",
            background: "linear-gradient(135deg, #667eea 0%, #1570EF 100%)",
            color: "#fff",
            py: 2,
          }}
        >
          Create Support Ticket
        </DialogTitle>

        {/* Form inside scrollable box */}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <TicketPage
            onClose={() => setOpenForm(false)}
            setSubmitRef={(fn) => (submitTicketRef.current = fn)}
          />
        </Box>
      </Dialog>
    </Box>
  );
};

export default TicketHistory;
